import { Info } from 'lucide-react'
import type { TestCasePriority } from '@/types'
import { PRIORITY_BADGE } from '@/lib/priorityBadge'
import { Tooltip } from '@/components/ui'

/**
 * Inline priority (P0–P3) dropdown — the one control both the Tests screen
 * (`TestDetail`) and the proposed-candidate card (`CandidatesSection`) use to
 * edit a test's priority in place (#2126). Styled as the tinted priority
 * badge it replaces (via the shared `PRIORITY_BADGE` map), so a read-only
 * badge and an editable dropdown look identical across surfaces.
 *
 * Presentational only: it holds no state and knows nothing about which
 * entity it edits. The caller passes the current value and an `onChange`
 * that fires the correct save (the two screens hit different endpoints).
 *
 * `allowUnset` adds a "not specified" option and lets `onChange` receive
 * `null` — for candidates, whose priority is nullable (absence means the
 * proposer's inference didn't run). Planner tests always have a priority,
 * so they leave it off and the dropdown offers the four bands only.
 *
 * A legend tooltip sits beside the dropdown: the P0–P3 bands were never
 * explained on screen. Since both surfaces render this one control, the
 * explanation lands on both at once.
 */
const PRIORITIES: readonly TestCasePriority[] = ['P0', 'P1', 'P2', 'P3']

/** What the bands mean, in plain terms. Grounded in `inferPriority`
 * (P0 = core happy path, down to P3 = lowest). */
const PRIORITY_LEGEND = (
  <div className="space-y-1">
    <p className="font-medium">How important this test is — highest first.</p>
    <ul className="space-y-0.5">
      <li>P0 — critical</li>
      <li>P1 — high</li>
      <li>P2 — medium</li>
      <li>P3 — low</li>
    </ul>
  </div>
)

interface Props {
  value: TestCasePriority | null | undefined
  onChange: (value: TestCasePriority | null) => void
  allowUnset?: boolean
  disabled?: boolean
  title?: string
}

export function PrioritySelect({ value, onChange, allowUnset = false, disabled, title }: Props) {
  const tint = value ? PRIORITY_BADGE[value] : 'text-muted-foreground border-border'
  return (
    <span className="inline-flex items-center gap-1">
      <select
        value={value ?? ''}
        disabled={disabled}
        title={title ?? 'Priority'}
        aria-label="Priority"
        onChange={(e) => onChange(e.target.value === '' ? null : (e.target.value as TestCasePriority))}
        className={`px-2 py-0.5 text-xs font-semibold rounded-md border cursor-pointer appearance-none outline-none disabled:cursor-not-allowed disabled:opacity-60 ${tint}`}
      >
        {allowUnset && <option value="">— Not specified —</option>}
        {PRIORITIES.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      <Tooltip content={PRIORITY_LEGEND} triggerLabel="What the priority levels mean" side="bottom">
        <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </Tooltip>
    </span>
  )
}
